/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

import { Inject, Injectable } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'
import { UnespCoreEnvironment } from 'src/libs/unesp-core'
import { catchError, identity, map, Observable, shareReplay, tap, throwError } from 'rxjs'
import { Concurso } from '../models/concurso'
import { ErrorHandlerService } from './error-handler.service'
import { Campus } from '../models/campus'
import { ConcursoTitulo } from '../models/concurso-titulo'

@Injectable({
  providedIn: 'root',
})
export class ConcursoService {
  private baseUrl: string
  private concursosCache$: Observable<{ id: number; titulo: string }[]> | null = null
  private concursosCacheTimestamp = 0
  private campusCache$: Observable<Campus[]> | null = null
  private readonly CACHE_TTL = 60 * 60 * 1000 // 1 hora

  constructor(
    private http: HttpClient,
    private errorHandlerService: ErrorHandlerService,
    @Inject('environment') env: UnespCoreEnvironment
  ) {
    this.baseUrl = env.baseUrlApi
  }

  gravar(form: Concurso): Observable<any> {
    return this.http
      .post<string>(`${this.baseUrl}/concurso`, form)
      .pipe(tap(() => this.invalidarCacheConcursos()), identity, this.errorHandlerService.catchError())
  }

  get(id: number): Observable<Concurso> {
    return this.http
      .get<Concurso>(`${this.baseUrl}/concurso/${id}`)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  getTitulo(id: number | undefined): Observable<ConcursoTitulo> {
    console.log(id)
    return this.http
      .get<ConcursoTitulo>(`${this.baseUrl}/concurso/${id}/titulo`)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  atualizar(id: number, form: Concurso): Observable<Concurso> {
    return this.http
      .put<Concurso>(`${this.baseUrl}/concurso/${id}`, form)
      .pipe(tap(() => this.invalidarCacheConcursos()), identity, this.errorHandlerService.catchError())
  }

  listar(
    search = '',
    page = 0,
    size = 10,
    filtroCacpSelecionado = 'TODOS',
    filtroCcptSelecionado = 'TODOS',
    filtroAndamentoSelecionado = '',
    filtroTipoConcursoSelecionado = '',
    sortField = 'campus',
    sortDirection = 'asc'
  ): Observable<any> {
    return this.http
      .get<any>(`${this.baseUrl}/concurso`, {
        params: new HttpParams()
          .set('search', search)
          .set('page', page.toString())
          .set('size', size.toString())
          .set('filtroCacp', filtroCacpSelecionado)
          .set('filtro', filtroCcptSelecionado)
          .set('filtroAndamento', filtroAndamentoSelecionado)
          .set('tipoConcurso', filtroTipoConcursoSelecionado)
          .set('sortField', sortField)
          .set('sortDirection', sortDirection),
      })
      .pipe(identity, this.errorHandlerService.catchError())
  }

  excluir(id: number): Observable<Concurso> {
    return this.http
      .delete<Concurso>(`${this.baseUrl}/concurso/${id}`)
      .pipe(tap(() => this.invalidarCacheConcursos()), identity, this.errorHandlerService.catchError())
  }

  camposHabilitados(): Observable<Campus[]> {
    if (!this.campusCache$) {
      this.campusCache$ = this.http
        .get<Campus[]>(`${this.baseUrl}/concurso/unidades-habilitadas`)
        .pipe(shareReplay(1), this.errorHandlerService.catchError())
    }
    return this.campusCache$
  }

  validarProcessoConcurso(fnParams: { concursoId?: number | null; processo: string }): Observable<Object> {
    const { processo, concursoId } = fnParams

    let params: any = { processo }

    if (concursoId) {
      params = { ...params, concursoId }
    }

    return this.http
      .get(`${this.baseUrl}/concurso/validar-processo`, { params })
      .pipe(identity, this.errorHandlerService.catchError())
  }

  /**
   * Retorna lista de todos os concursos (id + titulo) com cache.
   * Cache é invalidado automaticamente ao criar/editar/excluir concurso.
   */
  listarTodos(): Observable<{ id: number; titulo: string }[]> {
    if (!this.concursosCache$ || Date.now() - this.concursosCacheTimestamp > this.CACHE_TTL) {
      this.concursosCacheTimestamp = Date.now()
      this.concursosCache$ = this.http
        .get<any>(`${this.baseUrl}/concurso`, {
          params: new HttpParams().set('page', '0').set('size', '500').set('search', ''),
        })
        .pipe(
          map((data: any) => (data.content || []).map((c: any) => ({ id: c.id, titulo: c.titulo }))),
          catchError(err => { this.invalidarCacheConcursos(); return throwError(() => err) }),
          shareReplay(1),
          this.errorHandlerService.catchError()
        )
    }
    return this.concursosCache$
  }

  invalidarCacheConcursos(): void {
    this.concursosCache$ = null
  }
}
